import {MoneyLabel} from "../../../../../../components/general/MoneyLabel.tsx";
import {Money} from "../../../../../../lib/Money.ts";
import {DEFAULT_CURRENCY} from "../dndTypes.ts";
import {formatDate, getSignedAmountFromDirection} from "../utils/utils.ts";
import { cleanDocumentFileName } from "../../../../../../lib/TFICLib.ts";
import type {DocumentDirection} from "../../../../../../generated/enums.ts";

interface DragOverlayTransaction {
    relation_name: string;
    desc: string;
    total_amount_payable: Money;
    currency?: string;
}

interface DragOverlayDocument {
    relation_name: string;
    file_name: string;
    total_amount_payable: Money;
    currency?: string;
    document_direction?: DocumentDirection;
}

interface DragOverlayAttachedItem {
    relation_name: string;
    amount: Money;
    metadata?: string;
    date?: string;
    currency?: string;
    document_category?: string;
    document_direction?: DocumentDirection;
}

interface DragOverlayProps {
    activeTransaction?: DragOverlayTransaction | null;
    activeDocument?: DragOverlayDocument | null;
    activeAttachedItem?: DragOverlayAttachedItem | null;
}

export function DragOverlay({
    activeTransaction,
    activeDocument,
    activeAttachedItem,
}: DragOverlayProps) {
    if (activeTransaction) {
        return (
            <div className="p-3 border border-gray-200 rounded-md bg-white shadow-2xl "
            style={{ transform: "rotate(-1.5deg)" }}>
                <div className="text-base font-medium text-gray-900">
                    {activeTransaction.relation_name}
                </div>
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                    {activeTransaction.desc}
                </p>
                <MoneyLabel
                    value={activeTransaction.total_amount_payable.signed_amount / 100}
                    colorAid={true}
                    currency={
                        activeTransaction.total_amount_payable.currency ||
                        activeTransaction.currency ||
                        DEFAULT_CURRENCY
                    }
                    showCurrency={true}
                />
            </div>
        );
    }

    if (activeDocument) {
        const signedAmount = getSignedAmountFromDirection(
            activeDocument.total_amount_payable,
            activeDocument.document_direction
        );
        return (
            <div
                className="p-3 border border-gray-200 rounded-md bg-white shadow-2xl"
                style={{ transform: "rotate(1.5deg)" }}
            >
                <div className="text-base font-medium text-gray-900">
                    {activeDocument.relation_name}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                    { cleanDocumentFileName(activeDocument.file_name)}
                </div>
                <MoneyLabel
                    value={signedAmount / 100}
                    colorAid={true}
                    currency={
                        activeDocument.total_amount_payable.currency ||
                        activeDocument.currency ||
                        DEFAULT_CURRENCY
                    }
                    showCurrency={true}
                />
            </div>
        );
    }

    if (activeAttachedItem) {
        const getDisplayParts = () => {
            if (!activeAttachedItem.metadata || !activeAttachedItem.document_category) {
                return {reference: null, filename: activeAttachedItem.metadata};
            }
            const parts = activeAttachedItem.metadata.split(" ");
            const reference = parts.length ? parts[0] : null;
            const filename = parts.length > 1 ? parts.slice(1).join(" ") : null;
            return {
                reference,
                filename: filename ? cleanDocumentFileName(filename) : null,
            };
        };

        const {reference, filename} = getDisplayParts();
        const signedAmount = getSignedAmountFromDirection(
            activeAttachedItem.amount,
            activeAttachedItem.document_direction
        );

        return (
            <div
                className="p-3 border border-slate-200 rounded-md bg-slate-50 shadow-lg max-w-md"
                style={{
                    transform: activeAttachedItem.document_direction
                        ? "rotate(-1.5deg)"
                        : "rotate(1.5deg)",
                }}
            >
                <div className="text-sm font-medium text-gray-900">
                    {activeAttachedItem.relation_name}
                </div>
                {filename && (
                    <div className="text-xs text-gray-600 mt-1 truncate">
                        {filename}
                    </div>
                )}
                {reference && (
                    <div className="text-[11px] text-gray-500 mt-1">
                        {reference}
                    </div>
                )}
                {!filename && activeAttachedItem.metadata && (
                    <div className="text-xs text-gray-500 mt-1">
                        {activeAttachedItem.metadata}
                    </div>
                )}
                {activeAttachedItem.date && (
                    <div className="text-xs text-gray-500 mt-1">
                        {formatDate(activeAttachedItem.date)}
                    </div>
                )}
                <MoneyLabel
                    value={signedAmount / 100}
                    colorAid={true}
                    currency={
                        activeAttachedItem.amount.currency ||
                        activeAttachedItem.currency ||
                        DEFAULT_CURRENCY
                    }
                    showCurrency={true}
                />
            </div>
        );
    }

    return null;
}
